import { cn } from "@scribenote/brian-design-system";
import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type Ref,
} from "react";

import {
  entryVersion,
  flowchart,
  labelContext,
  labelsFor,
  type CatalogIndex,
  type EntryLabel,
  type FlowNode,
} from "./catalogGraph";

interface Wire {
  from: string;
  to: string;
  d: string;
}

const edgeKey = (from: string, to: string) => `${from}\0${to}`;

const FlowCard = ({
  node,
  label,
  inspected,
  anchor,
  onClick,
  ref,
}: {
  node: FlowNode;
  label: EntryLabel | undefined;
  inspected: boolean;
  anchor: boolean;
  onClick: () => void;
  ref: Ref<HTMLButtonElement>;
}) => (
  <button
    ref={ref}
    type="button"
    aria-current={inspected || undefined}
    onClick={onClick}
    className={cn(
      "block w-full min-w-0 rounded-paper border p-2.5 text-left transition-colors duration-150",
      "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
      inspected
        ? "border-accent bg-tint-accent"
        : anchor
          ? "border-ink bg-card hover:bg-smudge"
          : "border-rule bg-card hover:bg-smudge",
    )}
  >
    <span className="block text-annotation-caps text-annotation">
      {node.role}
    </span>
    <span className="mt-1 block break-words text-sm font-medium text-ink">
      {label?.name}
    </span>
    {label && labelContext(label) && (
      <span className="mt-0.5 block break-words text-xs text-annotation">
        {labelContext(label)}
      </span>
    )}
    {node.nestedCount > 0 && (
      <span className="mt-1.5 block text-xs text-ink-body">
        {node.nestedCount} nested
      </span>
    )}
  </button>
);

/** Source right side to target left side; several wires on one side spread along it in the other ends' vertical order. */
const route = (
  box: DOMRect,
  rects: Map<string, DOMRect>,
  edges: { from: string; to: string }[],
): Wire[] => {
  const shown = edges.filter(
    (edge) => rects.has(edge.from) && rects.has(edge.to),
  );
  const middle = (id: string) => {
    const rect = rects.get(id);
    return rect ? rect.top + rect.height / 2 : 0;
  };
  const ports = (key: "from" | "to", other: "from" | "to") => {
    const sides = new Map<string, string[]>();
    shown.forEach((edge) =>
      sides.set(edge[key], [...(sides.get(edge[key]) ?? []), edge[other]]),
    );
    sides.forEach((ids) => ids.sort((a, b) => middle(a) - middle(b)));
    return (id: string, toward: string) => {
      const rect = rects.get(id);
      const ids = sides.get(id) ?? [];
      if (!rect) return 0;
      return (
        rect.top + (rect.height * (ids.indexOf(toward) + 1)) / (ids.length + 1)
      );
    };
  };
  const outPort = ports("from", "to");
  const inPort = ports("to", "from");
  const round = (value: number) => Math.round(value * 10) / 10;
  return shown.map(({ from, to }) => {
    const x1 = round((rects.get(from)?.right ?? 0) - box.left);
    const x2 = round((rects.get(to)?.left ?? 0) - box.left);
    const y1 = round(outPort(from, to) - box.top);
    const y2 = round(inPort(to, from) - box.top);
    const bend = round(Math.max(Math.abs(x2 - x1) / 2, 24));
    return {
      from,
      to,
      d: `M ${x1} ${y1} C ${x1 + bend} ${y1}, ${x2 - bend} ${y2}, ${x2} ${y2}`,
    };
  });
};

export const RelationshipMap = ({
  anchor,
  index,
  scope,
  inspected,
  onInspect,
}: {
  anchor: string;
  index: CatalogIndex;
  scope: string;
  inspected: string;
  onInspect: (id: string) => void;
}) => {
  const chart = useMemo(() => flowchart(anchor, index), [anchor, index]);
  const labels = useMemo(
    () =>
      labelsFor(
        chart.columns.flat().map((node) => node.id),
        index,
      ),
    [chart, index],
  );
  const scrollerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const nodes = useRef(new Map<string, HTMLButtonElement>());
  const [wires, setWires] = useState<Wire[]>([]);
  const markerId = useId().replace(/[^\w-]/g, "");

  useLayoutEffect(() => {
    const content = contentRef.current;
    if (!content) return;
    const measure = () => {
      const rects = new Map(
        [...nodes.current].map(([id, node]) => [
          id,
          node.getBoundingClientRect(),
        ]),
      );
      const next = route(content.getBoundingClientRect(), rects, chart.edges);
      setWires((previous) =>
        previous.length === next.length &&
        previous.every(
          (wire, i) =>
            wire.from === next[i].from &&
            wire.to === next[i].to &&
            wire.d === next[i].d,
        )
          ? previous
          : next,
      );
    };
    const observer = new ResizeObserver(measure);
    observer.observe(content);
    nodes.current.forEach((node) => observer.observe(node));
    measure();
    return () => observer.disconnect();
  }, [chart]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    const node = nodes.current.get(anchor)?.getBoundingClientRect();
    if (!scroller || !node || scroller.scrollWidth <= scroller.clientWidth)
      return;
    scroller.scrollLeft +=
      node.left -
      scroller.getBoundingClientRect().left -
      (scroller.clientWidth - node.width) / 2;
  }, [anchor]);

  const highlighted = useMemo(() => {
    const out = new Map<string, typeof chart.edges>();
    const incoming = new Map<string, typeof chart.edges>();
    chart.edges.forEach((edge) => {
      out.set(edge.from, [...(out.get(edge.from) ?? []), edge]);
      incoming.set(edge.to, [...(incoming.get(edge.to) ?? []), edge]);
    });
    const versionOf = (id: string) => {
      const entry = index.entries.get(id);
      return entry ? entryVersion(entry) : null;
    };
    const lit = new Set<string>();
    const reach = (backward: boolean) => {
      const queue = [{ id: inspected, version: versionOf(inspected) }];
      const seen = new Set<string>();
      while (queue.length > 0) {
        const state = queue.pop()!;
        const stateKey = `${state.id}\0${state.version ?? ""}`;
        if (seen.has(stateKey)) continue;
        seen.add(stateKey);
        for (const edge of (backward ? incoming : out).get(state.id) ?? []) {
          const next = backward ? edge.from : edge.to;
          const nextVersion = versionOf(next);
          if (state.version && nextVersion && state.version !== nextVersion)
            continue;
          lit.add(edgeKey(edge.from, edge.to));
          queue.push({ id: next, version: state.version ?? nextVersion });
        }
      }
    };
    reach(false);
    reach(true);
    return lit;
  }, [chart, index, inspected]);
  const lit = (wire: Wire) => highlighted.has(edgeKey(wire.from, wire.to));

  return (
    <div className="space-y-3">
      <div
        ref={scrollerRef}
        role="group"
        aria-label="Relationship flowchart"
        className="overflow-x-auto pb-2"
      >
        <div
          ref={contentRef}
          className="relative flex w-max min-w-full items-center gap-12 p-1"
        >
          <svg
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 h-full w-full overflow-visible"
          >
            <defs>
              {(["off", "on"] as const).map((state) => (
                <marker
                  key={state}
                  id={`${markerId}-${state}`}
                  viewBox="0 0 10 10"
                  refX="10"
                  refY="5"
                  markerWidth="8"
                  markerHeight="8"
                  markerUnits="userSpaceOnUse"
                  orient="auto"
                >
                  <path
                    d="M 0 0 L 10 5 L 0 10 z"
                    className={
                      state === "on" ? "fill-accent" : "fill-rule-strong"
                    }
                  />
                </marker>
              ))}
            </defs>
            {[...wires]
              .sort((a, b) => Number(lit(a)) - Number(lit(b)))
              .map((wire) => (
                <path
                  key={`${wire.from}>${wire.to}`}
                  d={wire.d}
                  fill="none"
                  strokeWidth={lit(wire) ? 2 : 1.25}
                  className={lit(wire) ? "stroke-accent" : "stroke-rule-strong"}
                  markerEnd={`url(#${markerId}-${lit(wire) ? "on" : "off"})`}
                />
              ))}
          </svg>
          {chart.columns.map((column, position) => (
            <ul
              key={position}
              className="relative flex w-52 shrink-0 flex-col gap-3"
            >
              {column.map((node) => (
                <li key={node.id}>
                  <FlowCard
                    ref={(element) => {
                      if (element) nodes.current.set(node.id, element);
                      return () => {
                        nodes.current.delete(node.id);
                      };
                    }}
                    node={node}
                    label={labels.get(node.id)}
                    inspected={node.id === inspected}
                    anchor={node.id === anchor}
                    onClick={() => onInspect(node.id)}
                  />
                </li>
              ))}
            </ul>
          ))}
          {chart.edges.length === 0 && (
            <p className="max-w-xs text-xs text-annotation">
              No linked definition in {scope}.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
